/**
 * The pane when the list beside it has no row to point at.
 *
 * Five causes, and the difference matters: a trainer with no programmes needs to know how one gets
 * here, a trainer whose filters matched nothing needs to know the programmes are still there, and
 * neither is a read that is still open. `rows.length === 0` cannot tell those apart, which is why
 * the cause arrives as a value rather than being inferred.
 *
 * ── STANDING is drawn, and it is drawn as the pane it is about to be ─────────────────────────
 * An earlier revision left `pending` and `error` as an empty box: 55% of the viewport, blank, and
 * identical to each other. The box was right — it is what keeps the list beside it from moving —
 * but a reserve with nothing in it is not a designed state, it is the absence of one.
 *
 * So the wait is the pane's own shape: the cover plate with its two figures, the seven day tiles,
 * the muscle band. `shared/ui/skeleton`'s bar and its sheen, which is the same treatment
 * `ListStates` draws the rows beside it with — one skeleton treatment in this app, not two. The
 * FAILURE is that same shape with `skel.still`, which takes the sweep off, under the neutral mark:
 * a pane that has stopped waiting and has nothing to show. It carries no sentence and no second
 * `Spróbuj ponownie`, because the list card beside it is already saying both and two controls for
 * one failure is worse than one.
 *
 * The shape's three heights are the section reserves themselves, so the skeleton is exactly as tall
 * as the loaded pane and a library that lands changes ink and never geometry.
 *
 * **The outline itself is gated, not only its sweep** (21 September 2026). It used to be drawn from
 * frame 0, so a library that answered in 90ms put a whole pane of bars on screen and took them off
 * again — the flicker the 150ms grace exists to prevent, drawn by the one surface that ignored it.
 * `.standing` holds the height on its own, so during the grace the reserve simply stands empty.
 *
 * ── The three EMPTIES get the app's own empty state ──────────────────────────────────────────
 * `shared/ui/EmptyState` with a drawing, which is what the list card uses 400px away — two
 * anatomies for one situation was the thing to fix. `tone: 'action'` where something is missing and
 * there is somewhere to go; `neutral` where the trainer has plans and the act is on another screen.
 *
 * **The filtered case carries the mark and no sentence.** The list card has just said "nothing
 * matched" and offered the way out; the pane repeating it made three sentences for one situation.
 */

import { useTranslation } from 'react-i18next';

import { EmptyState } from '~/shared/ui/EmptyState';
import { PlanMarkIcon } from '~/shared/ui/EmptyStateIcons';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { InspectorAbsence } from '../model/planListStatus';
import styles from './Inspector.module.css';

/** The two figures the cover plate prints, and the seven days of the pattern week. */
const KPIS = [0, 1];
const TILES = [0, 1, 2, 3, 4, 5, 6];

/** The sentence each kind of nothing carries, and the tone it is said in. */
const EMPTIES: Readonly<
  Record<'noPlans' | 'noClientPlans', { readonly key: string; readonly tone: 'action' | 'neutral' }>
> = {
  noPlans: { key: 'web:planLibrary.inspector.empty.noTemplates', tone: 'action' },
  // The clients scope's own fact, in the words the card beside it already uses: a trainer whose
  // clients are on no plan is not a trainer with no programmes, and telling them to add one sends
  // them to a screen they may have eight rows on.
  noClientPlans: { key: 'web:planLibrary.clientPlans.empty.title', tone: 'neutral' },
};

interface PaneShapeProps {
  /**
   * Whether the outline belongs on screen at all. `false` is the gate's 150ms grace: `.standing`
   * holds the pane's whole height on its own, so the reserve stands empty and a library that
   * answers quickly draws no bars to flash away. The gate hides the BARS, never the box.
   */
  readonly bars: boolean;
  /** `false` takes the sweep and the fade off: a failure is not a wait. */
  readonly waiting: boolean;
}

/**
 * The pane's own outline in bars — plate, figures, tiles, band.
 *
 * The three KPI boxes are plain fills rather than bars of their own: they sit ON the plate, and a
 * bar inside a bar runs a second sweep across the first one's.
 */
function PaneShape({ bars, waiting }: PaneShapeProps) {
  if (!bars) return null;

  const bar = `${String(skel.bar)} ${waiting ? '' : String(skel.still)}`;

  return (
    <div className={styles.shape}>
      <div className={`${bar} ${String(styles.shapeHero)}`}>
        <span className={styles.shapeKpis}>
          {KPIS.map((index) => (
            <i key={index} className={styles.shapeKpi} />
          ))}
        </span>
      </div>
      <div className={styles.shapeWeek}>
        {TILES.map((index) => (
          <i key={index} className={`${bar} ${String(styles.shapeTile)}`} />
        ))}
      </div>
      <div className={styles.shapeMuscle}>
        <i className={`${bar} ${String(styles.shapeBand)}`} />
      </div>
    </div>
  );
}

interface InspectorEmptyProps {
  readonly reason: InspectorAbsence;
  /**
   * The LIST gate's answer for the wait this pane is standing in. It decides only whether the
   * outline is DRAWN — a failure has no grace window to sit inside and always draws its shape.
   */
  readonly skeleton: boolean;
}

export function InspectorEmpty({ reason, skeleton }: InspectorEmptyProps) {
  const { t } = useTranslation();

  if (reason === 'pending' || reason === 'error') {
    // No `aria-busy` of its own: the PANE carries one value for the whole card, over both its
    // waits. Two nested regions reporting busy separately is two answers to one question.
    return (
      <div className={styles.standing}>
        <PaneShape bars={reason === 'error' || skeleton} waiting={reason === 'pending'} />
        {reason === 'pending' ? (
          // `visuallyHidden` is `position: fixed`, so the word a listener needs costs no layout.
          <span className={hidden.srOnly}>{t('web:planLibrary.states.loading')}</span>
        ) : (
          <span className={styles.standingMark} aria-hidden>
            <PlanMarkIcon />
          </span>
        )}
      </div>
    );
  }

  if (reason === 'filtered') {
    return (
      <div className={styles.empty}>
        {/* The mark alone: the list card beside it has just said what matched nothing. */}
        <span className={styles.standingMark} aria-hidden>
          <PlanMarkIcon />
        </span>
      </div>
    );
  }

  const empty = EMPTIES[reason];
  return (
    <div className={styles.empty}>
      <EmptyState mark={<PlanMarkIcon />} tone={empty.tone} title={t(empty.key)} />
    </div>
  );
}
